import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { Chatbot } from "@/components/Chatbot";
import { CookieConsent } from "@/components/CookieConsent";
import { lazy, Suspense } from "react";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load all route components for better code splitting (including Index for optimal bundle size)
const Index = lazy(() => import("./pages/Index"));
const Advertisers = lazy(() => import("./pages/Advertisers"));
const Drivers = lazy(() => import("./pages/Drivers"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Apply = lazy(() => import("./pages/Apply"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DriverPortal = lazy(() => import("./pages/DriverPortal"));
const AdvertiserPortal = lazy(() => import("./pages/AdvertiserPortal"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AnalyticsLayout = lazy(() => import("./pages/admin/analytics/AnalyticsLayout"));
const DriversAnalytics = lazy(() => import("./pages/admin/analytics/DriversAnalytics"));
const AdvertisersAnalytics = lazy(() => import("./pages/admin/analytics/AdvertisersAnalytics"));
const RevenueAnalytics = lazy(() => import("./pages/admin/analytics/RevenueAnalytics"));
const ChatbotAnalytics = lazy(() => import("./pages/admin/analytics/ChatbotAnalytics"));
const SystemHealthAnalytics = lazy(() => import("./pages/admin/analytics/SystemHealthAnalytics"));
const RateLimits = lazy(() => import("./pages/admin/RateLimits"));
const KnowledgeBase = lazy(() => import("./pages/admin/KnowledgeBase"));
const ContactSubmissions = lazy(() => import("./pages/admin/ContactSubmissions"));
const PortalNotifications = lazy(() => import("./pages/admin/PortalNotifications"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const SecurityAudit = lazy(() => import("./pages/admin/SecurityAudit"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/advertisers" element={<Advertisers />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/driver-portal" element={<DriverPortal />} />
            <Route path="/advertiser-portal" element={<AdvertiserPortal />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/analytics" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AnalyticsLayout />
                </ProtectedRoute>
              }
            >
              <Route path="drivers" element={<DriversAnalytics />} />
              <Route path="advertisers" element={<AdvertisersAnalytics />} />
              <Route path="revenue" element={<RevenueAnalytics />} />
              <Route path="chatbot" element={<ChatbotAnalytics />} />
              <Route path="system-health" element={<SystemHealthAnalytics />} />
            </Route>
            <Route 
              path="/admin/rate-limits" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <RateLimits />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/knowledge-base" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <KnowledgeBase />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/contact-submissions" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ContactSubmissions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/portal-notifications" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <PortalNotifications />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/security-audit" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <SecurityAudit />
                </ProtectedRoute>
              } 
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Chatbot />
        <CookieConsent />
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
    </GoogleReCaptchaProvider>
  </QueryClientProvider>
);

export default App;
